//@ts-nocheck
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@banno/jha-wc/src/tables/jha-table/jha-table";
import "@banno/jha-wc/src/tables/jha-table-row/jha-table-row";
import "@banno/jha-wc/src/tables/jha-table-header/jha-table-header";
import "@banno/jha-wc/src/tables/jha-table-cell/jha-table-cell";
import "@banno/jha-wc/src/forms/jha-form-select/jha-form-select";
import "@banno/jha-wc/src/forms/jha-form-email-input/jha-form-email-input";
import "@banno/jha-wc/src/forms/jha-form-input-base/jha-form-input";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/icons/jha-icon-plus";
import "@banno/jha-wc/src/icons/jha-icon-edit";
import "@banno/jha-wc/src/icons/jha-icon-delete";
import "@banno/jha-wc/src/icons/jha-icon-close";
import "@banno/jha-wc/src/icons/jha-icon-circle-checkmark-filled";
import "@banno/jha-wc/src/jha-tooltip/jha-tooltip";
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import { getRequest } from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import { getRoleData } from "../../utils/getRoleData";
import { getEnvApiUrl } from "../../utils/app-utils";
import "../../Components/TemplateStringInput";
import { WorkflowTemplateVariable } from "../../types/WorkflowTemplates/WorkflowTemplateVariable";

/**
 * Notification interface for internal use
 */
interface TemplateNotification {
  number: number;
  messageType: string;
  notificationType: string;
  to: string;
  subject: string;
  template: string;
  isEditing: boolean;
  isNew: boolean;
  // Store original values for cancel operation
  originalValues?: {
    messageType: string;
    notificationType: string;
    to: string;
    subject: string;
    template: string;
  };
}

/**
 * Workflow Template Notification View
 *
 * A notification management component specifically designed for workflow templates.
 * Features inline editing with edit/delete action icons similar to the Variables grid.
 *
 * Features:
 * - Add new notifications
 * - Inline edit existing notifications
 * - Delete notifications
 * - Validation for required fields
 * - Read-only mode support
 */
@customElement("workflow-template-notification-view")
export class WorkflowTemplateNotificationView extends formElementMixin(LitElement) {
  static get is() {
    return "workflow-template-notification-view";
  }

  @property({ type: Array }) notifications: TemplateNotification[] = [];
  @property({ type: Boolean }) isReadOnly: boolean = false;
  @property({ type: Array }) variables: WorkflowTemplateVariable[] = [];

  // Dropdown options loaded from API
  @state() messageTypeOptions: any[] = [
    { name: "Success", value: "Success" },
    { name: "Failure", value: "Failure" },
  ];
  @state() notificationTypeOptions: any[] = [{ name: "Email", value: "Email" }];
  @state() optionsLoaded: boolean = false;

  static readonly styles = css`
    :host {
      display: block;
    }

    /* Table styling - matching Variables grid */
    jha-table {
      width: 100%;
      border-collapse: collapse;
    }

    jha-table-header {
      font-weight: 600;
      padding: 12px;
      text-align: left;
      white-space: nowrap;
      background: var(--jh-color-container-primary-enabled, rgb(244, 244, 244));
      border-top: 1px solid var(--jh-color-divider-primary);
      vertical-align: middle;
    }

    jha-table-cell {
      padding: 12px;
      vertical-align: top;
      border-left: 1px solid var(--jha-advanced-table-cell-border);
      font-size: 15px;
      line-height: 1.4;
    }

    jha-table-row {
      border-bottom: 1px solid;
    }

    /* Form inputs in editing mode - consistent sizing */
    jha-form-select,
    jha-form-input,
    jha-form-email-input,
    template-string-input {
      width: 100%;
      min-width: 120px;
      display: block;
    }

    /* Wrapper for form elements to ensure consistent height and alignment */
    .input-cell-wrapper {
      min-height: 78px;
      display: flex;
      flex-direction: column;
    }

    /* Action icons - centered in cell */
    .action-icons {
      display: flex;
      gap: 8px;
      align-items: center;
      justify-content: center;
      width: 100%;
    }

    /* Action cell - centered content */
    .action-cell {
      text-align: center;
    }

    /* When row is in editing mode, align content with the input field area (below label) */
    /* jha-form-select/template-string-input: label is ~18px, we want to align with input center */
    .editing-row-content {
      display: inline-block;
      margin-top: 36px;
    }

    .action-icon {
      cursor: pointer;
      width: 20px;
      height: 20px;
      color: var(--jha-color-primary, #0066cc);
      transition: opacity 0.2s;
    }

    .action-icon:hover {
      opacity: 0.7;
    }

    .action-icon[disabled] {
      cursor: not-allowed;
      opacity: 0.3;
    }

    jha-icon-edit {
      color: var(--jha-color-primary, #0066cc);
    }

    jha-icon-delete {
      color: var(--jha-color-danger, #dc3545);
    }

    jha-icon-close {
      color: var(--jha-color-secondary, #6c757d);
    }

    jha-icon-circle-checkmark-filled {
      color: var(--jha-color-success, #28a745);
    }

    #addNotificationContainer {
      margin-top: 16px;
      display: flex;
      justify-content: flex-end;
    }

    /* Cell display value for saved rows */
    .cell-display {
      word-break: break-word;
    }

    /* Empty state message - matching Variables grid */
    .no-notifications-message {
      padding: 32px;
      text-align: center;
      border: 1px solid;
      border-top: none;
    }

    /* Light mode - matches Variables grid styling */
    @media (prefers-color-scheme: light) {
      .no-notifications-message {
        color: #666666;
        border-color: #e0e0e0;
      }

      jha-table-row {
        border-bottom-color: #e0e0e0;
      }
    }

    /* Dark mode - matches Variables grid styling */
    @media (prefers-color-scheme: dark) {
      .no-notifications-message {
        color: #a0a0a0;
        border-color: #3a3a3a;
      }

      jha-table {
        border: 1px solid #3a3a3a;
      }

      jha-table-row {
        border-bottom-color: #3a3a3a;
      }

      jha-icon-edit {
        color: #4da6ff;
      }

      jha-icon-circle-checkmark-filled {
        color: #28a745;
      }

      jha-icon-delete {
        color: #ff6b6b;
      }

      jha-icon-close {
        color: #999999;
      }
    }
  `;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadDropdownOptions();
  }

  /**
   * Load message type and notification type options from API
   */
  async loadDropdownOptions() {
    try {
      // getEnvApiUrl: localhost-only; .env.local never ships — no effect in prod, test, staging, or CI builds.
      const [msgTypeRes, notTypeRes] = await Promise.all([
        getRequest(getEnvApiUrl(Apis.NOTIFICATION_MESSAGE_TYPE)),
        getRequest(getEnvApiUrl(Apis.NOTIFICATION_TYPE)),
      ]);

      const messageTypes = getRoleData(msgTypeRes?.list ?? []);
      const notificationTypes = getRoleData(notTypeRes?.list ?? []);

      // Only update if we got valid data from API
      if (messageTypes.length > 0 && notificationTypes.length > 0) {
        this.messageTypeOptions = messageTypes;
        this.notificationTypeOptions = notificationTypes;
      } else {
        console.warn("Failed to load notification options from API, using fallback values");
      }

      this.optionsLoaded = true;
    } catch (error) {
      console.error("Failed to load notification options:", error);
      // Fallback values are already set as property defaults
      this.optionsLoaded = true;
    }
  }

  /**
   * Add a new notification row in edit mode
   */
  onAddNotification() {
    // Check if there's already a row being edited
    const hasEditingRow = this.notifications.some((n) => n.isEditing);
    if (hasEditingRow) {
      return; // Don't add new row while another is being edited
    }

    const newNotification: TemplateNotification = {
      number: this.notifications.length + 1,
      messageType: "",
      notificationType: "",
      to: "",
      subject: "",
      template: "",
      isEditing: true,
      isNew: true,
    };

    this.notifications = [...this.notifications, newNotification];
    this.dispatchNotificationChange();
  }

  /**
   * Start editing a notification row
   */
  onEditNotification(index: number) {
    // Check if there's already a row being edited
    const hasEditingRow = this.notifications.some((n) => n.isEditing);
    if (hasEditingRow) {
      return; // Don't allow multiple edits at once
    }

    const notification = this.notifications[index];

    // Store original values for cancel
    notification.originalValues = {
      messageType: notification.messageType,
      notificationType: notification.notificationType,
      to: notification.to,
      subject: notification.subject,
      template: notification.template,
    };

    notification.isEditing = true;
    this.notifications = [...this.notifications];
    this.dispatchNotificationChange();
  }

  /**
   * Save notification changes
   */
  onSaveNotification(index: number) {
    const notification = this.notifications[index];

    // Read values directly from template-string-input elements using data attributes
    // This is needed because template-string-input only fires @change on blur
    // Using data-row-index and data-field attributes for robust element selection
    // (survives layout changes like adding/removing fields)
    const toInput = this.shadowRoot?.querySelector(
      `template-string-input[data-row-index="${index}"][data-field="to"]`
    ) as any;
    const subjectInput = this.shadowRoot?.querySelector(
      `template-string-input[data-row-index="${index}"][data-field="subject"]`
    ) as any;
    const templateInput = this.shadowRoot?.querySelector(
      `template-string-input[data-row-index="${index}"][data-field="template"]`
    ) as any;

    // Sync values from DOM elements if they exist (keep existing values if not rendered)
    if (toInput) notification.to = toInput.value ?? "";
    if (subjectInput) notification.subject = subjectInput.value ?? "";
    if (templateInput) notification.template = templateInput.value ?? "";

    // Validate required fields (trim to catch whitespace-only values)
    if (
      !notification.messageType ||
      !notification.notificationType ||
      !notification.to?.trim() ||
      !notification.subject?.trim() ||
      !notification.template?.trim()
    ) {
      // Don't save if validation fails - fields will show warnings
      return;
    }

    // Validate email format using the shared validation method
    // Supports multiple comma-separated emails and template variables
    if (!this.isEmailValid(notification.to)) {
      return;
    }

    // Normalize emails - trim leading/trailing spaces from each email
    notification.to = this.normalizeEmails(notification.to);

    // Mark as saved
    notification.isEditing = false;
    notification.isNew = false;
    delete notification.originalValues;

    this.notifications = [...this.notifications];
    this.dispatchNotificationChange();
  }

  /**
   * Cancel editing - restore original values or remove new row
   */
  onCancelNotification(index: number) {
    const notification = this.notifications[index];

    if (notification.isNew) {
      // Remove new unsaved row
      this.notifications = this.notifications.filter((_, i) => i !== index);
      // Renumber remaining notifications
      this.notifications.forEach((n, i) => (n.number = i + 1));
    } else {
      // Restore original values
      if (notification.originalValues) {
        notification.messageType = notification.originalValues.messageType;
        notification.notificationType = notification.originalValues.notificationType;
        notification.to = notification.originalValues.to;
        notification.subject = notification.originalValues.subject;
        notification.template = notification.originalValues.template;
      }
      notification.isEditing = false;
      delete notification.originalValues;
      this.notifications = [...this.notifications];
    }

    this.dispatchNotificationChange();
  }

  /**
   * Delete a notification
   */
  onDeleteNotification(index: number) {
    this.notifications = this.notifications.filter((_, i) => i !== index);
    // Renumber remaining notifications
    this.notifications.forEach((n, i) => (n.number = i + 1));
    this.dispatchNotificationChange();
  }

  /**
   * Handle field value changes
   */
  onFieldChange(index: number, field: keyof TemplateNotification, value: string) {
    this.notifications[index][field] = value;
    this.notifications = [...this.notifications];
    this.dispatchNotificationChange();
  }

  /**
   * Dispatch notification change event to parent
   */
  dispatchNotificationChange() {
    // Check if there's an unsaved/editing row
    const hasUnsavedRow = this.notifications.some((n) => n.isEditing || n.isNew);

    this.dispatchEvent(
      new CustomEvent("notification-change", {
        detail: {
          notifications: this.notifications,
          hasUnsavedRow,
          isValid: !hasUnsavedRow,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Get variable options formatted for template-string-input (sorted alphabetically)
   */
  get variableOptions() {
    return (this.variables ?? [])
      .map((v) => ({
        name: v.name,
        value: v.name,
        description: v.description,
      }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }

  /**
   * Handle variable-added event from template-string-input
   * Bubble up to parent so it can add to Settings tab
   */
  onVariableAdded(e: CustomEvent) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent("variable-added", {
        detail: e.detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Check if a notification row is valid
   */
  isNotificationValid(notification: TemplateNotification): boolean {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const hasVariable = /\{[a-zA-Z0-9][a-zA-Z0-9_\-]*\}/.test(notification.to);
    const isValidEmail = emailPattern.test(notification.to) || hasVariable;

    return !!(
      notification.messageType &&
      notification.notificationType &&
      notification.to &&
      isValidEmail &&
      notification.subject &&
      notification.template
    );
  }

  /**
   * Normalize email string by trimming each email and removing extra whitespace
   * Converts "  admin@example.com ,  support@example.com  " to "admin@example.com,support@example.com"
   */
  normalizeEmails(email: string): string {
    if (!email || email.trim() === "") {
      return "";
    }
    return email
      .split(",")
      .map((e) => e.trim())
      .filter((e) => e.length > 0)
      .join(",");
  }

  /**
   * Check if email format is valid (either valid email or contains template variable)
   * Supports multiple comma-separated emails (e.g., "admin@example.com,support@example.com")
   * 
   * NOTE: This validation is intentionally permissive for template variables.
   * Patterns like "{Username}@company.com" or "support@{Domain}" are allowed here
   * because the actual email validation occurs at workflow generation time when
   * the user provides real values for the variables. At template creation time,
   * we cannot validate partially-variablized emails.
   */
  isEmailValid(email: string): boolean {
    if (!email || email.trim() === "") {
      return true; // Empty is handled by required validation, not format validation
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const variablePattern = /\{[a-zA-Z0-9][a-zA-Z0-9_\-]*\}/;

    // Split by comma and validate each email
    const emails = email.split(",").map((e) => e.trim());
    return emails.every((singleEmail) => {
      if (!singleEmail) return false; // Empty segment after split is invalid
      // Valid if it's a proper email OR contains a template variable
      return emailPattern.test(singleEmail) || variablePattern.test(singleEmail);
    });
  }

  /**
   * Get email validation error text if email is invalid
   */
  getEmailErrorText(email: string): string {
    if (!email || email.trim() === "") {
      return "Email (To) is required";
    }
    if (!this.isEmailValid(email)) {
      return "Invalid email format. Use name@domain.com, multiple emails separated by commas, or a {variable}";
    }
    return "";
  }

  /**
   * Render a notification row
   */
  renderNotificationRow(notification: TemplateNotification, index: number) {
    const isEditing = notification.isEditing;
    const canEdit = !this.isReadOnly;

    return html`
      <jha-table-row>
        <!-- Number -->
        <jha-table-cell>
          <span class="${isEditing ? "editing-row-content" : ""}">${notification.number}</span>
        </jha-table-cell>

        <!-- Message Type -->
        <jha-table-cell>
          ${isEditing
        ? html`
                <div class="input-cell-wrapper">
                  <jha-form-select
                    outline
                    required
                    warning="Message Type is required"
                    label="Message Type"
                    data-auto-id="notification-messageType"
                    .options=${this.messageTypeOptions}
                    labelKey="name"
                    valueKey="value"
                    emptyOptionLabel="Select Type"
                    .value=${notification.messageType}
                    @change=${(e: Event) => this.onFieldChange(index, "messageType", (e.target as any).value)}>
                  </jha-form-select>
                </div>
              `
        : html`<span>${notification.messageType}</span>`}
        </jha-table-cell>

        <!-- Notification Type -->
        <jha-table-cell>
          ${isEditing
        ? html`
                <div class="input-cell-wrapper">
                  <jha-form-select
                    outline
                    required
                    warning="Notification Type is required"
                    label="Notification Type"
                    data-auto-id="notification-notificationType"
                    .options=${this.notificationTypeOptions}
                    labelKey="name"
                    valueKey="value"
                    emptyOptionLabel="Select Type"
                    .value=${notification.notificationType}
                    @change=${(e: Event) => this.onFieldChange(index, "notificationType", (e.target as any).value)}>
                  </jha-form-select>
                </div>
              `
        : html`<span>${notification.notificationType}</span>`}
        </jha-table-cell>

        <!-- To (Email) - supports template variables -->
        <jha-table-cell>
          ${isEditing
        ? html`
                <div class="input-cell-wrapper">
                  <jha-tooltip bottom text="Enter valid Email Address to receive notification. Use '{' for variables. Max length 256 characters.">
                    <template-string-input
                      data-row-index="${index}"
                      data-field="to"
                      data-auto-id="notification-to"
                      label="Email (To)"
                      placeholder="name@domain.com or {variable}"
                      maxlength="256"
                      .required=${true}
                      .value=${notification.to}
                      .options=${this.variableOptions}
                      .errorText=${this.getEmailErrorText(notification.to)}
                      .forceShowWarning=${notification.to && !this.isEmailValid(notification.to)}
                      variableButtonTooltipPosition="top-left"
                      @change=${(e: CustomEvent) => this.onFieldChange(index, "to", e.detail.value)}
                      @variable-added=${this.onVariableAdded}>
                    </template-string-input>
                  </jha-tooltip>
                </div>
              `
        : html`<span class="cell-display">${notification.to}</span>`}
        </jha-table-cell>

        <!-- Subject - supports template variables -->
        <jha-table-cell>
          ${isEditing
        ? html`
                <div class="input-cell-wrapper">
                  <jha-tooltip bottom text="Enter information to display in subject of notification. Allowed characters . _ - , ; @ ( ). Use '{' for variables. Max length 255 characters.">
                    <template-string-input
                      data-row-index="${index}"
                      data-field="subject"
                      data-auto-id="notification-subject"
                      label="Subject"
                      placeholder="Notification subject or {variable}"
                      maxlength="255"
                      .required=${true}
                      .value=${notification.subject}
                      .options=${this.variableOptions}
                      variableButtonTooltipPosition="top-left"
                      @change=${(e: CustomEvent) => this.onFieldChange(index, "subject", e.detail.value)}
                      @variable-added=${this.onVariableAdded}>
                    </template-string-input>
                  </jha-tooltip>
                </div>
              `
        : html`<span class="cell-display">${notification.subject}</span>`}
        </jha-table-cell>

        <!-- Template Message - supports template variables -->
        <jha-table-cell>
          ${isEditing
        ? html`
                <div class="input-cell-wrapper">
                  <jha-tooltip bottom text="Enter information to display in body of notification. Allowed characters . _ - , ; @ ( ). Use '{' for variables. Max length 255 characters.">
                    <template-string-input
                      data-row-index="${index}"
                      data-field="template"
                      data-auto-id="notification-template"
                      label="Template Message"
                      placeholder="Message template or {variable}"
                      maxlength="255"
                      .required=${true}
                      .value=${notification.template}
                      .options=${this.variableOptions}
                      variableButtonTooltipPosition="top-left"
                      @change=${(e: CustomEvent) => this.onFieldChange(index, "template", e.detail.value)}
                      @variable-added=${this.onVariableAdded}>
                    </template-string-input>
                  </jha-tooltip>
                </div>
              `
        : html`<span class="cell-display">${notification.template}</span>`}
        </jha-table-cell>

        <!-- Actions -->
        ${canEdit
        ? html`
              <jha-table-cell class="action-cell">
                <div class="action-icons ${isEditing ? "editing-row-content" : ""}">
                  ${isEditing
            ? html`
                        <jha-tooltip top text="Save Notification">
                          <jha-icon-circle-checkmark-filled class="action-icon" data-auto-id="notification-save" @click=${() => this.onSaveNotification(index)}>
                          </jha-icon-circle-checkmark-filled>
                        </jha-tooltip>
                        <jha-tooltip top text="Cancel">
                          <jha-icon-close class="action-icon" data-auto-id="notification-cancel" @click=${() => this.onCancelNotification(index)}> </jha-icon-close>
                        </jha-tooltip>
                      `
            : html`
                        <jha-tooltip left text="Edit Notification">
                          <jha-icon-edit class="action-icon" data-auto-id="notification-edit" @click=${() => this.onEditNotification(index)}> </jha-icon-edit>
                        </jha-tooltip>
                        <jha-tooltip left text="Delete Notification">
                          <jha-icon-delete class="action-icon" data-auto-id="notification-delete" @click=${() => this.onDeleteNotification(index)}> </jha-icon-delete>
                        </jha-tooltip>
                      `}
                </div>
              </jha-table-cell>
            `
        : ""}
      </jha-table-row>
    `;
  }

  /**
   * Render the table header row
   */
  renderTableHeader(canEdit: boolean) {
    return html`
      <jha-table-row>
        <jha-table-header style="width: 60px;">NO.</jha-table-header>
        <jha-table-header style="width: 140px;">MESSAGE TYPE</jha-table-header>
        <jha-table-header style="width: 160px;">NOTIFICATION TYPE</jha-table-header>
        <jha-table-header style="min-width: 200px;">TO</jha-table-header>
        <jha-table-header style="min-width: 180px;">SUBJECT</jha-table-header>
        <jha-table-header style="min-width: 200px;">TEMPLATE MESSAGE</jha-table-header>
        ${canEdit ? html`<jha-table-header style="width: 80px; text-align: center;">ACTIONS</jha-table-header>` : ""}
      </jha-table-row>
    `;
  }

  render() {
    const canEdit = !this.isReadOnly;
    const hasEditingRow = this.notifications.some((n) => n.isEditing);

    return html`
      <div>
        ${this.notifications.length > 0
        ? html`
              <!-- Table with notifications -->
              <jha-table>
                ${this.renderTableHeader(canEdit)}
                ${this.notifications.map((notification, index) => this.renderNotificationRow(notification, index))}
              </jha-table>
            `
        : html`
              <!-- Empty state: Show table header then "No Notifications" message below -->
              <jha-table> ${this.renderTableHeader(canEdit)} </jha-table>
              <div class="no-notifications-message">
                <h3>No Notifications Found</h3>
              </div>
            `}
        ${canEdit
        ? html`
              <div id="addNotificationContainer">
                <jha-tooltip left text="Add a new notification">
                  <jha-button
                    type="button"
                    primary
                    sync
                    ?disabled=${hasEditingRow}
                    data-auto-id="addNotificationBtn"
                    @click=${this.onAddNotification}>
                    <jha-icon-plus slot="icon-left"></jha-icon-plus>
                    Add Notification
                  </jha-button>
                </jha-tooltip>
              </div>
            `
        : ""}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "workflow-template-notification-view": WorkflowTemplateNotificationView;
  }
}
